<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="bg-white shadow rounded-lg mb-6">
        <div class="px-6 py-4 flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Mis Facturas</h1>
            <p class="text-sm text-gray-500">Historial de facturas generadas</p>
          </div>
          <div class="flex space-x-3">
            <router-link :to="{ name: 'cliente-ordenes' }"
              class="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100">
              Mis Órdenes
            </router-link>
            <button @click="logout" class="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200">
              Salir
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <svg class="animate-spin h-10 w-10 text-indigo-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
        <p class="text-gray-600">Cargando facturas...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p class="text-red-600 mb-3">{{ errorMsg }}</p>
        <button @click="cargarFacturas" class="text-sm text-red-700 underline hover:text-red-800">Reintentar</button>
      </div>

      <!-- Sin facturas -->
      <div v-else-if="facturas.length === 0" class="bg-white shadow rounded-lg p-12 text-center">
        <p class="text-gray-500 text-lg mb-4">Aún no tienes facturas generadas.</p>
        <router-link :to="{ name: 'cliente-ordenes' }" class="text-indigo-600 hover:text-indigo-500">
          Ir a mis órdenes para facturar →
        </router-link>
      </div>

      <!-- Lista de facturas -->
      <div v-else class="space-y-4">
        <div v-for="factura in facturas" :key="factura.id" class="bg-white shadow rounded-lg overflow-hidden">
          <div class="px-6 py-4">
            <div class="flex items-center justify-between mb-2">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">{{ factura.serie }}-{{ factura.folio }}</h3>
                <p class="text-xs text-gray-500 font-mono">UUID: {{ factura.uuid }}</p>
              </div>
              <div class="text-right">
                <p class="text-xl font-bold text-gray-900">${{ formatMonto(factura.total) }}</p>
                <p class="text-sm text-gray-500">{{ formatFecha(factura.fecha) }}</p>
              </div>
            </div>
            <div class="flex items-center justify-between mt-3 pt-3 border-t">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {{ factura.estatus }}
              </span>
              <div class="flex space-x-2">
                <button @click="descargarPDF(factura)" :disabled="descargando[factura.id + '_pdf']"
                  class="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 disabled:opacity-50">
                  {{ descargando[factura.id + '_pdf'] ? '...' : 'PDF' }}
                </button>
                <button @click="descargarXML(factura)" :disabled="descargando[factura.id + '_xml']"
                  class="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 disabled:opacity-50">
                  {{ descargando[factura.id + '_xml'] ? '...' : 'XML' }}
                </button>
              </div>
            </div>
            <p v-if="errorDescarga[factura.id]" class="text-sm text-red-600 mt-2">{{ errorDescarga[factura.id] }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import facturacionCliente from '@/Services/facturacionCliente'
import { useClienteAuthStore } from '@/stores/clienteAuth'

export default {
  name: 'MisFacturas',
  data() {
    return {
      facturas: [],
      loading: true,
      error: false,
      errorMsg: '',
      descargando: {},
      errorDescarga: {}
    }
  },
  mounted() {
    const store = useClienteAuthStore()
    if (!store.isAuthenticated) {
      this.$router.push({ name: 'cliente-login' })
      return
    }
    this.cargarFacturas()
  },
  methods: {
    async cargarFacturas() {
      this.loading = true
      this.error = false
      const res = await facturacionCliente.getFacturas()
      if (res.success) {
        this.facturas = res.data || []
      } else {
        if (res.status === 401) {
          const store = useClienteAuthStore()
          store.logout()
          this.$router.push({ name: 'cliente-login' })
          return
        }
        this.error = true
        this.errorMsg = res.message
      }
      this.loading = false
    },
    async descargarPDF(factura) {
      this.descargando = { ...this.descargando, [factura.id + '_pdf']: true }
      this.errorDescarga = { ...this.errorDescarga, [factura.id]: '' }
      try {
        const res = await facturacionCliente.descargarPDF(factura.id)
        const blob = new Blob([res.data], { type: 'application/pdf' })
        this.triggerDownload(blob, `factura_${factura.folio}.pdf`)
      } catch (e) {
        this.errorDescarga = { ...this.errorDescarga, [factura.id]: 'Error al descargar PDF.' }
      } finally {
        this.descargando = { ...this.descargando, [factura.id + '_pdf']: false }
      }
    },
    async descargarXML(factura) {
      this.descargando = { ...this.descargando, [factura.id + '_xml']: true }
      this.errorDescarga = { ...this.errorDescarga, [factura.id]: '' }
      try {
        const res = await facturacionCliente.descargarXML(factura.id)
        const blob = new Blob([res.data], { type: 'application/xml' })
        this.triggerDownload(blob, `factura_${factura.folio}.xml`)
      } catch (e) {
        this.errorDescarga = { ...this.errorDescarga, [factura.id]: 'Error al descargar XML.' }
      } finally {
        this.descargando = { ...this.descargando, [factura.id + '_xml']: false }
      }
    },
    triggerDownload(blob, filename) {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    },
    logout() {
      const store = useClienteAuthStore()
      store.logout()
      this.$router.push({ name: 'cliente-login' })
    },
    formatFecha(f) {
      if (!f) return ''
      return new Date(f).toLocaleDateString('es-MX')
    },
    formatMonto(m) {
      if (!m) return '0.00'
      return parseFloat(m).toLocaleString('es-MX', { minimumFractionDigits: 2 })
    }
  }
}
</script>
